import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { supabase } from '../../lib/supabaseClient'

// MomentsArchivePage — /moments/archive
//
// Chronological grid of past prompts (revealed + late-pair) with one
// thumbnail per row drawn from the viewer's own capture for that day.
// Tap → /moments/:promptId for the full pair + reactions + comments.
//
// One signed URL per row is enough for the thumbnail; partner images
// are loaded on the day-detail page rather than minting double URLs
// here.
export function MomentsArchivePage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const [rows, setRows] = useState([])
  const [thumbs, setThumbs] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return undefined
    let alive = true
    ;(async () => {
      setLoading(true)
      const { data: prompts } = await supabase
        .from('moments_prompts')
        .select('id, prompt_date, status')
        .in('status', ['revealed', 'open', 'expired'])
        .order('prompt_date', { ascending: false })
        .limit(200)
      const promptList = prompts || []
      if (!promptList.length) {
        if (alive) { setRows([]); setLoading(false) }
        return
      }
      const { data: mine } = await supabase
        .from('moments_captures')
        .select('prompt_id, image_path, late')
        .eq('user_id', user.id)
        .in('prompt_id', promptList.map((p) => p.id))
      const mineByPrompt = {}
      for (const m of mine || []) mineByPrompt[m.prompt_id] = m
      const merged = promptList
        .map((p) => ({ ...p, mine: mineByPrompt[p.id] || null }))
        .filter((p) => p.mine)
      if (!alive) return
      setRows(merged)
      setLoading(false)

      const signs = {}
      for (const r of merged) {
        const { data } = await supabase.storage
          .from('moments')
          .createSignedUrl(r.mine.image_path, 60 * 60)
        if (data?.signedUrl) signs[r.id] = data.signedUrl
      }
      if (alive) setThumbs((prev) => ({ ...prev, ...signs }))
    })()
    return () => { alive = false }
  }, [user])

  if (!authLoading && user && !hasOwnerAccess(user)) {
    return <Navigate to="/" replace />
  }
  if (authLoading || loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin opacity-60" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-md space-y-4 p-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/moments')}
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
          aria-label={t('moments.back')}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 dir="auto" className="text-lg font-semibold">{t('moments.archiveTitle')}</h1>
      </div>
      {!rows.length ? (
        <p dir="auto" className="pt-10 text-center text-sm opacity-70">{t('moments.archiveEmpty')}</p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {rows.map((r) => {
            const d = new Date(r.prompt_date + 'T00:00:00')
            let label = r.prompt_date
            try {
              label = d.toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-US', {
                month: 'short', day: 'numeric',
              })
            } catch { /* keep raw */ }
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => navigate(`/moments/${r.id}`)}
                className="relative aspect-square overflow-hidden rounded-xl bg-neutral-200 dark:bg-neutral-800"
              >
                {thumbs[r.id] ? (
                  <img src={thumbs[r.id]} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="absolute inset-0 grid place-items-center">
                    <Loader2 className="h-4 w-4 animate-spin opacity-60" />
                  </div>
                )}
                <div className="absolute bottom-1 start-1 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white">
                  <span dir="auto">{label}</span>
                </div>
                {r.status !== 'revealed' && (
                  <div className="absolute end-1 top-1 rounded-full bg-amber-500/90 px-1.5 py-0.5 text-[9px] font-medium text-white">
                    <span dir="auto">{r.status === 'expired' ? t('moments.archiveMissed') : t('moments.archiveSolo')}</span>
                  </div>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
